import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Target, Zap, Heart } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

export default function Results() {
  const { t } = useTranslation();
  const results = [
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: t('results.quick_start.title'),
      description: t('results.quick_start.description')
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: t('results.long_term.title'),
      description: t('results.long_term.description')
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: t('results.more_energy.title'),
      description: t('results.more_energy.description')
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: t('results.harmony.title'),
      description: t('results.harmony.description')
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-12 sm:py-16 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-white">
            {t('results.title')}
          </motion.h2>
          <motion.p variants={item} className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            {t('results.subtitle')}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {results.map((result, index) => (
            <motion.div
              key={result.title}
              variants={item}
              className="text-center group"
            >
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/10 hover:bg-white/15 transition-all hover:scale-105 h-full flex flex-col justify-between">
                <div className="text-rose-300 mb-4 flex justify-center">
                  {result.icon}
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                  {result.title}
                </h3>
                <p className="text-gray-200 text-xs sm:text-sm">
                  {result.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          variants={item}
          className="mt-12 text-center"
        >
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/10">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4">
              {t('results.cta.title')}
            </h3>
            <p className="text-gray-200 mb-6 max-w-2xl mx-auto text-sm sm:text-base">
              {t('results.cta.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              <span className="bg-rose-400/20 text-rose-200 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm">
                ✓ {t('results.features.personal')}
              </span>
              <span className="bg-rose-400/20 text-rose-200 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm">
                ✓ {t('results.features.scientific')}
              </span>
              <span className="bg-rose-400/20 text-rose-200 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm">
                ✓ {t('results.features.support')}
              </span>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
